import TopHeader from "components/common/header";
import Agenda from "components/templates/common/Discuss/Detail/Agenda";
import VoteResult from "components/templates/common/Discuss/Detail/Vote/Result";
import TotalVoter from "components/templates/common/Discuss/Detail/Vote/TotalVoter";
import VoteWaiting from "components/templates/common/Discuss/Detail/Vote/Waiting";
import { useRouter } from "next/router";
import { useDiscussDetail } from "utils/clientAPIs/discuss";
import DiscussDetailHeader from "./Header";
import { useEffect, useState, useCallback, useRef } from "react";
import OpinionListIndex from "components/templates/common/Discuss/Detail/Debate/Index";
import { DebateProvider } from "contexts/DebateContext";
import { useSession } from "next-auth/react";
import DebateAlarmRegisterModal from "components/modals/DebateAlarmRegisterModal";
import CustomToast from "components/atoms/CustomToast";
import Research from "components/templates/common/Discuss/Detail/Research";
import DiscussDetailMetaHead from "./MetaHead";
import FloatingButton from "components/templates/common/Discuss/Detail/FloatingButton";
import VoteTitle from "components/templates/common/Discuss/Detail/Title";
import DiscussNoti from "components/templates/common/Discuss/Detail/Noti";
import DiscussDivider from "components/templates/common/Discuss/Detail/Divider";
import DiscussMoreButtons from "components/templates/common/Discuss/Detail/MoreButtons";
import useDiscussAlarm from "utils/hooks/DiscussAlarm";
import { DiscussIdContext } from "contexts/DiscussIdContext";
import ReportThumb from "components/templates/common/components/ReportThumb";
import DividerLine from "components/common/divider/line";
import { TYPO } from "styles/Typo";
import Button from "components/common/buttons/buttons";
import BriefingAd from "components/templates/common/main/feed/BriefingAd";
import ResentAgenda from "components/templates/common/Discuss/Detail/Debate/ResentAgenda";
import { TAILWIND_TO_CSS } from "utils/constant/STYLE";
import { TrackedModalProvider } from "lib/analytics/react/modal";
import AdMock from "components/ads/AdMock";

function DiscussDetailPage({
  isAdminUser,
  isMobile,
  isLoginUser,
  email,
  discussId,
  slug,
}: {
  isAdminUser: boolean;
  isMobile: boolean;
  isLoginUser: boolean;
  email?: string;
  discussId: string;
  slug: string;
}) {
  // if 발행 대기 전 -> 관리지가 아닌 경우 return <div></div>;
  const router = useRouter();
  const [waitingHeight, setWaitingHeight] = useState<number | null>(null);
  const [animation, setAnimation] = useState<boolean>(false);
  const [alarmTrigger, setAlarmTrigger] = useState<"NEW" | "COMMENT" | false>(
    false
  );
  const [alarmModal, setAlarmModal] = useState<"NEW" | "COMMENT" | false>(
    false
  );
  const [alarmModalDisplayInitial, setAlarmModalDisplayInitial] =
    useState(false);
  const [detailDiscussData, isLoading, submitVote, mutate] = useDiscussDetail({
    discussId: Number(discussId),
    setAnimation,
    setAlarmTrigger,
    email,
    isLoginUser,
  });
  // 투표 마감 상태, 유저 투표 상태, 투표 선택지 타입
  let isClosed = detailDiscussData?.isClosed;
  let userChoice = detailDiscussData?.userChoice;
  const userChoiceRef = useRef(userChoice);
  const isClosedRef = useRef(isClosed);
  useEffect(() => {
    userChoiceRef.current = userChoice;
    isClosedRef.current = isClosed;
  }, [userChoice, isClosed]);

  // 토론 알림 관리
  const {
    DebateAlarmOnStatus,
    isValid,
    debateAlarmRegister,
    alarmRegisterToast,
    setAlarmRegisterToast,
  } = useDiscussAlarm();

  const voteSectionRef = useRef<HTMLDivElement>(null);
  const [floatButtonStats, setFloatButtonStats] = useState<"VOTE" | "SHARE">(
    "SHARE"
  );

  const reportLastObserverRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (alarmModal) {
      setAlarmModalDisplayInitial(true);
    }
  }, [alarmModal]);

  useEffect(() => {
    if (detailDiscussData?.userChoice) {
      setFloatButtonStats("SHARE");
    }
  }, [detailDiscussData?.userChoice]);

  useEffect(() => {
    if (isLoading) return;
    // if (!isLoginUser) return;

    // 리포트 마지막 요소 관찰
    reportLastObserverRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !alarmModalDisplayInitial) {
          setAlarmTrigger("NEW");
          setAlarmModalDisplayInitial(true);
        }
      },
      {
        threshold: 0,
      }
    );

    // DOM 요소가 렌더링될 때까지 잠시 대기
    const setupObservers = () => {
      const reportLastArea = document.getElementById("report-last-area");

      if (reportLastArea) {
        reportLastObserverRef.current?.observe(reportLastArea);
      }
    };

    // 마크다운 렌더링이 완료될 때까지 잠시 대기
    const timeoutId = setTimeout(() => {
      setupObservers();

      // 만약 여전히 요소가 없다면 MutationObserver로 DOM 변화 감지
      if (document.querySelectorAll("h2").length < 3) {
        const observer = new MutationObserver(() => {
          if (document.querySelectorAll("h2").length >= 3) {
            // console.log("h2 요소 감지됨, Observer 재설정");
            setupObservers();
            observer.disconnect();
          }
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true,
        });

        // 5초 후 MutationObserver 정리
        setTimeout(() => {
          observer.disconnect();
        }, 5000);
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isLoading, isLoginUser, alarmModalDisplayInitial]);

  useEffect(() => {
    let timer: any = null;

    if (alarmTrigger === "NEW" && !DebateAlarmOnStatus?.isNewAgendaAlarmOn) {
      const hideVoteAlarmRegister = localStorage.getItem(
        "hideNewDebateAlarmRegister"
      );
      if (hideVoteAlarmRegister && new Date(hideVoteAlarmRegister) > new Date())
        return;

      timer = setTimeout(() => {
        setAlarmModal(alarmTrigger);
      }, 1000);
    }
    if (
      alarmTrigger === "COMMENT" &&
      !DebateAlarmOnStatus?.isUserActionAlarmOn
    ) {
      const hideCommentAlarmRegister = localStorage.getItem(
        "hideCommentAlarmRegister"
      );
      if (
        hideCommentAlarmRegister &&
        new Date(hideCommentAlarmRegister) > new Date()
      )
        return;

      timer = setTimeout(() => {
        setAlarmModal(alarmTrigger);
      }, 200);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [alarmTrigger, DebateAlarmOnStatus]);

  useEffect(() => {
    if (!router.isReady) return;

    if ("newag" in router.query) {
      router.replace(`${router.asPath}&utm_source=sms&utm_medium=text`);
    }
  }, [router.isReady]);

  if (isLoading)
    return (
      <main className="min-w-[360px] max-w-[800px] w-screen bg-white min-h-screen">
        <DiscussDetailMetaHead detailDiscussData={detailDiscussData} />
        <TopHeader
          clickHandler={() => {
            router.back();
          }}
          bgColor={TAILWIND_TO_CSS["gray2"]}
        />
      </main>
    );

  return (
    <DiscussIdContext.Provider value={{ discussId, slug }}>
      <main className="min-w-[360px] max-w-[800px] w-screen bg-white min-h-screen mb-4">
        <DiscussDetailMetaHead detailDiscussData={detailDiscussData} />
        <AdMock variant="sideRail" />
        <DiscussDetailHeader
          mutate={mutate}
          isMobile={isMobile}
          DebateAlarmOnStatus={DebateAlarmOnStatus}
          debateAlarmRegister={debateAlarmRegister}
        />

        <div className="flex flex-col">
          <ReportThumb
            data={detailDiscussData}
            className="w-full bg-gray20 discuss-top-thumb"
            id="discuss-top-thumb"
          />
          <Agenda
            detailDiscussData={detailDiscussData}
            isMobile={isMobile}
            DebateAlarmOnStatus={DebateAlarmOnStatus}
            debateAlarmRegister={debateAlarmRegister}
          />
          <div className="px-6 py-4">
            <div className="h-[1px] bg-gray15 w-full" />
          </div>
          <Research
            detailDiscussData={detailDiscussData}
            isLoginUser={isLoginUser}
          />
          <div className="h-9" />
          <div
            className="pt-9 px-6 flex gap-5 flex-col items-center justify-center"
            style={{
              display: DebateAlarmOnStatus?.isNewAgendaAlarmOn
                ? "none"
                : "flex",
            }}
          >
            <div className="">
              <p className={`${TYPO.TYPO7_1_RG} text-gray60 text-center`}>
                놓치면 안되는 증시 리포트
              </p>
              <p className={`${TYPO.TYPO7_MD} text-gray70 text-center`}>
                리스크웨더 AI가 매일 보내드려요
              </p>
            </div>
            <Button
              type="secondary"
              size="md"
              text="문자로 알림 받기"
              clickHandler={() => {
                debateAlarmRegister(
                  false,
                  "NEW",
                  "report_alarm_cta",
                  Number(discussId)
                );
              }}
            />
          </div>
          <DiscussNoti />
          <DividerLine size="md" />
          <div className="">
            <AdMock variant="intent" className="mx-0 px-6" />
            <BriefingAd type="discuss" />
            <ResentAgenda discussId={Number(discussId)} />
            <div className="h-4" />
          </div>
          <DividerLine size="md" />
          <div>
            <p className={`${TYPO.TYPO5_SB} text-gray90 px-6 pt-6`}>토론</p>

            <div
              className={`flex flex-col gap-4 px-6 py-4  `}
              id="vote-section"
            >
              <VoteTitle detailDiscussData={detailDiscussData} />

              {!isClosed && !userChoice ? (
                <div
                  className="flex justify-center items-center "
                  ref={voteSectionRef}
                >
                  <VoteWaiting
                    detailDiscussData={detailDiscussData}
                    submitVote={submitVote}
                    setWaitingHeight={setWaitingHeight}
                    mutate={mutate}
                  />
                </div>
              ) : (
                <VoteResult
                  detailDiscussData={detailDiscussData}
                  waitingHeight={waitingHeight}
                  mutate={mutate}
                  animation={animation}
                />
              )}
              <div className="flex flex-col ">
                <TotalVoter detailDiscussData={detailDiscussData} />
              </div>
            </div>
          </div>
        </div>
        <DiscussDivider />

        <DebateProvider
          isLoginUser={isLoginUser}
          detailDiscussData={detailDiscussData}
          setAlarmTrigger={setAlarmTrigger}
        >
          {/* <DiscussMoreButtons data={detailDiscussData} /> */}
          <OpinionListIndex
            isMobile={isMobile}
            issueAt={detailDiscussData?.issue_at}
          />
          <FloatingButton
            status={floatButtonStats}
            data={detailDiscussData}
            submitVote={submitVote}
          />
        </DebateProvider>
        {alarmModal && (
          <TrackedModalProvider
            modalId="report_bs_alarm_cta"
            isOpen={alarmModal === "NEW"}
            triggerSectionId=""
            triggerType="auto"
          >
            <DebateAlarmRegisterModal
              setModalOpen={setAlarmModal}
              isModalOpen={alarmModal}
              debateAlarmRegister={debateAlarmRegister}
            />
          </TrackedModalProvider>
        )}
        <CustomToast
          message="이제 문자로 새 글 알림을 받아요"
          isVisible={alarmRegisterToast}
          onClose={() => setAlarmRegisterToast(false)}
          isSuccess={true}
          width={260}
          successIconFill={"#34BB7A"}
        />
      </main>
    </DiscussIdContext.Provider>
  );
}

export default DiscussDetailPage;
